// src/components/dialogs/DeviceDetailsDialog.jsx
import React from "react";
import {
  Dialog,
  Box,
  Typography,
  Stack,
  Divider,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
} from "@mui/material";

import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import VerifiedIcon from "@mui/icons-material/Verified";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import StoreIcon from "@mui/icons-material/Store";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import HistoryIcon from "@mui/icons-material/History";
import AddIcon from "@mui/icons-material/Add";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import QrCodeIcon from "@mui/icons-material/QrCode";
import TagIcon from "@mui/icons-material/Tag";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SmsIcon from "@mui/icons-material/Sms";

import { formatDateTime, moneyINR } from "../../../utils/helpers";

/* ─────────────────────────────────────────────
   Keyframe — sheet entry
───────────────────────────────────────────── */
const ANIM = `
  @keyframes sheetUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

/* ─────────────────────────────────────────────
   Warranty status badge
───────────────────────────────────────────── */
function WarrantyBadge({ isApproved, expiryDate }) {
  const expired = expiryDate && new Date(expiryDate) <= new Date();
  const active  = isApproved && !expired;
  return (
    <Chip
      icon={
        active ? (
          <VerifiedIcon sx={{ fontSize: "12px !important", color: "#16a34a !important" }} />
        ) : (
          <ErrorOutlineIcon sx={{ fontSize: "12px !important", color: "#64748b !important" }} />
        )
      }
      label={active ? "Warranty Active" : expired ? "Expired" : "Inactive"}
      size="small"
      sx={{
        height: 22,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.3,
        bgcolor: active ? "#f0fdf4" : "#f1f5f9",
        color:  active ? "#15803d" : "#475569",
        border: `1px solid ${active ? "#bbf7d0" : "#e2e8f0"}`,
        borderRadius: 0.2,
        flexShrink: 0,
        "& .MuiChip-icon": { ml: "5px" },
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   Claim status chip (for history list)
───────────────────────────────────────────── */
function ClaimStatusChip({ status }) {
  const cfg = (() => {
    switch (status) {
      case "completed":
        return { label: "Completed", icon: <CheckCircleIcon />, bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" };
      case "rejected":
        return { label: "Rejected",  icon: <CancelIcon />,     bg: "#fef2f2", color: "#dc2626", border: "#fecaca" };
      case "approved":
        return { label: "Approved",  icon: <VerifiedIcon />,    bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" };
      case "otp_sent":
        return { label: "OTP Sent",  icon: <SmsIcon />,         bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" };
      case "pending":
        return { label: "Pending",   icon: <HourglassTopIcon />,bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" };
      default:
        return {
          label: status?.replace(/_/g, " ") ?? "—",
          icon: <HourglassTopIcon />,
          bg: "#f8fafc", color: "#475569", border: "#e2e8f0",
        };
    }
  })();
  return (
    <Chip
      icon={React.cloneElement(cfg.icon, {
        sx: { fontSize: "11px !important", color: `${cfg.color} !important` },
      })}
      label={cfg.label}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.2,
        flexShrink: 0,
        bgcolor: cfg.bg,
        color:   cfg.color,
        border:  `1px solid ${cfg.border}`,
        borderRadius: 0.2,
        "& .MuiChip-icon": { ml: "4px" },
      }}
    />
  );
}

/* ─────────────────────────────────────────────
   Section header — icon tile + title
───────────────────────────────────────────── */
function SectionHeader({ icon, title }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" mb={1.2}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 0.2,
          bgcolor: "#f0fdf4",
          border: "1px solid #dcfce7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 14, color: "#16a34a" } })}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, sm: 13 }, color: "#111827" }}>
        {title}
      </Typography>
    </Stack>
  );
}

/* ─────────────────────────────────────────────
   Icon + text info row
───────────────────────────────────────────── */
function InfoRow({ icon, label, value, href }) {
  const inner = (
    <Stack direction="row" spacing={0.8} alignItems="flex-start" py={0.5}>
      <Box sx={{ flexShrink: 0, mt: "1px", color: "#94a3b8", display: "flex" }}>
        {React.cloneElement(icon, { sx: { fontSize: 14, color: "#94a3b8" } })}
      </Box>
      <Box minWidth={0}>
        {label && (
          <Typography
            sx={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase" }}
          >
            {label}
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: { xs: 11, sm: 12 },
            fontWeight: 600,
            color: href ? "#2563eb" : "#374151",
            wordBreak: "break-word",
            lineHeight: 1.4,
          }}
        >
          {value || "—"}
        </Typography>
      </Box>
    </Stack>
  );
  if (href) {
    return (
      <Box component="a" href={href} sx={{ textDecoration: "none", display: "block" }}>
        {inner}
      </Box>
    );
  }
  return inner;
}

/* ─────────────────────────────────────────────
   Main dialog
───────────────────────────────────────────── */
export default function DeviceDetailsDialog({
  open,
  onClose,
  device,
  retailerInfo,
  deviceClaims = [],
  onRaiseClaim,
  onOpenClaim,
}) {
  /* ── Business logic — UNCHANGED ── */
  if (!device) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth
        sx={{
          "& .MuiDialog-paper": {
            margin: 0, width: "100%", maxWidth: "100%",
            borderRadius: "14px 14px 0 0",
            position: "fixed", bottom: 0,
          },
        }}
      >
        <Box p={3}>
          <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>No device selected</Typography>
        </Box>
      </Dialog>
    );
  }

  const statusLabel = device.is_approved ? "Warranty Active" : "Inactive";

  return (
    <>
      <style>{ANIM}</style>

      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            margin: 0,
            width: "100%",
            maxWidth: "100%",
            borderRadius: "14px 14px 0 0",
            position: "fixed",
            bottom: 0,
            maxHeight: { xs: "92svh", sm: "90vh" },
            boxShadow: "0 -8px 40px rgba(0,0,0,0.14)",
            animation: "sheetUp 0.28s cubic-bezier(0.32,0.72,0,1) both",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            paddingLeft:  "env(safe-area-inset-left)",
            paddingRight: "env(safe-area-inset-right)",
          },
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(3px)",
            bgcolor: "rgba(0,0,0,0.35)",
          },
        }}
      >

        {/* ── Drag handle ── */}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, flexShrink: 0 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: "#e2e8f0" }} />
        </Box>

        {/* ── Sticky header ── */}
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            pt: 1.2,
            pb: 1.4,
            borderBottom: "1px solid #f1f5f9",
            bgcolor: "#fff",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>

            {/* Left: icon tile + name + badge */}
            <Stack direction="row" spacing={1.2} alignItems="center" flex={1} minWidth={0}>
              <Box
                sx={{
                  width: { xs: 38, sm: 42 },
                  height: { xs: 38, sm: 42 },
                  borderRadius: 0.2,
                  bgcolor: "#f0fdf4",
                  border: "1px solid #dcfce7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <PhoneIphoneIcon sx={{ color: "#16a34a", fontSize: { xs: 20, sm: 22 } }} />
              </Box>

              <Box flex={1} minWidth={0}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: 14, sm: 15 },
                    color: "#111827",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  }}
                >
                  {device.product_name}
                </Typography>
                <Stack direction="row" spacing={0.8} alignItems="center" mt={0.4} flexWrap="wrap" gap={0.4}>
                  {device.model && (
                    <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: "#64748b" }}>
                      {device.model}
                    </Typography>
                  )}
                  <WarrantyBadge isApproved={device.is_approved} expiryDate={device.expiry_date} />
                </Stack>
              </Box>
            </Stack>

            {/* Close button */}
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 0.2,
                width: 34,
                height: 34,
                flexShrink: 0,
                "&:hover": { bgcolor: "#f1f5f9" },
              }}
            >
              <CloseIcon fontSize="small" sx={{ color: "#475569" }} />
            </IconButton>
          </Stack>
        </Box>

        {/* ── Scrollable body ── */}
        <Box
          sx={{
            overflowY: "auto",
            overflowX: "hidden",
            flexGrow: 1,
            bgcolor: "#f8fafc",
            px: { xs: 1.5, sm: 2 },
            pt: 1.8,
            pb: "max(24px, env(safe-area-inset-bottom, 24px))",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#e2e8f0", borderRadius: 99 },
          }}
        >
          <Stack spacing={1.5}>

            {/* ══════════════════
                STATS ROW
            ══════════════════ */}
            <Stack direction="row" spacing={1.2}>

              <Card elevation={0} sx={{ flex: 1, borderRadius: 0.2, border: "1px solid #e8f0fe", bgcolor: "#fff" }}>
                <CardContent sx={{ py: 1.2, px: { xs: 1.2, sm: 1.4 }, "&:last-child": { pb: 1.2 } }}>
                  <Stack direction="row" spacing={0.6} alignItems="center" mb={0.3}>
                    <AssignmentTurnedInIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                    <Typography sx={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase" }}>
                      Claim Balance
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 20 }, color: "#111827", lineHeight: 1 }}>
                    {device.available_claim ?? 0}
                  </Typography>
                </CardContent>
              </Card>

              <Card elevation={0} sx={{ flex: 1, borderRadius: 0.2, border: "1px solid #e8f0fe", bgcolor: "#fff" }}>
                <CardContent sx={{ py: 1.2, px: { xs: 1.2, sm: 1.4 }, "&:last-child": { pb: 1.2 } }}>
                  <Stack direction="row" spacing={0.6} alignItems="center" mb={0.3}>
                    <CurrencyRupeeIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                    <Typography sx={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase" }}>
                      Device Price
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: 15, sm: 17 },
                      color: "#111827",
                      lineHeight: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {moneyINR(device.device_price || 0)}
                  </Typography>
                </CardContent>
              </Card>

            </Stack>

            {/* ══════════════════
                DEVICE ID PILL
            ══════════════════ */}
            {(device.serial || device.imei1) && (
              <Stack
                direction="row"
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #e8f0fe",
                  borderRadius: 0.2,
                  overflow: "hidden",
                }}
              >
                {device.imei1 && (
                  <Stack direction="row" spacing={0.6} alignItems="center" flex={1}
                    sx={{ px: { xs: 1.2, sm: 1.4 }, py: 1 }}
                  >
                    <FingerprintIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
                    <Box minWidth={0}>
                      <Typography sx={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase" }}>
                        IMEI
                      </Typography>
                      <Typography sx={{ fontSize: { xs: 10, sm: 11 }, fontWeight: 600, color: "#374151", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {device.imei1}
                      </Typography>
                    </Box>
                  </Stack>
                )}

                {device.imei1 && device.serial && (
                  <Divider orientation="vertical" flexItem sx={{ borderColor: "#e8f0fe" }} />
                )}

                {device.serial && (
                  <Stack direction="row" spacing={0.6} alignItems="center" flex={1}
                    sx={{ px: { xs: 1.2, sm: 1.4 }, py: 1 }}
                  >
                    <QrCodeIcon sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }} />
                    <Box minWidth={0}>
                      <Typography sx={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase" }}>
                        Serial
                      </Typography>
                      <Typography sx={{ fontSize: { xs: 10, sm: 11 }, fontWeight: 600, color: "#374151", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {device.serial}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Stack>
            )}

            {/* ══════════════════
                PURCHASE DETAILS
            ══════════════════ */}
            <Card elevation={0} sx={{ borderRadius: 0.2, border: "1px solid #e8f0fe", bgcolor: "#fff" }}>
              <CardContent sx={{ px: { xs: 1.4, sm: 1.6 }, pt: { xs: 1.4, sm: 1.6 }, pb: "14px !important" }}>
                <SectionHeader icon={<StoreIcon />} title="Purchase Details" />
                <Divider sx={{ borderColor: "#f1f5f9", mb: 1 }} />

                <Stack spacing={0.2}>
                  {retailerInfo?.business_name && (
                    <InfoRow icon={<StoreIcon />} label="Retailer" value={retailerInfo.business_name} />
                  )}
                  <InfoRow
                    icon={<AccessTimeIcon />}
                    label="Purchased"
                    value={formatDateTime(device.created_at)}
                  />
                  {retailerInfo?.contact_person && (
                    <InfoRow icon={<PersonIcon />} label="Contact" value={retailerInfo.contact_person} />
                  )}
                  {retailerInfo?.contact_phone && (
                    <InfoRow
                      icon={<PhoneIcon />}
                      label="Phone"
                      value={retailerInfo.contact_phone}
                      href={`tel:${retailerInfo.contact_phone}`}
                    />
                  )}
                  {(retailerInfo?.address_line1 || retailerInfo?.city) && (
                    <InfoRow
                      icon={<LocationOnIcon />}
                      label="Address"
                      value={[retailerInfo?.address_line1, retailerInfo?.city].filter(Boolean).join(", ")}
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* ══════════════════
                CLAIM HISTORY
            ══════════════════ */}
            <Card elevation={0} sx={{ borderRadius: 0.2, border: "1px solid #e8f0fe", bgcolor: "#fff" }}>
              <CardContent sx={{ px: { xs: 1.4, sm: 1.6 }, pt: { xs: 1.4, sm: 1.6 }, pb: "14px !important" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.2}>
                  <SectionHeader icon={<HistoryIcon />} title="Claim History" />
                  {deviceClaims.length > 0 && (
                    <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, mb: 1.2 }}>
                      {deviceClaims.length} claim{deviceClaims.length !== 1 ? "s" : ""}
                    </Typography>
                  )}
                </Stack>

                <Divider sx={{ borderColor: "#f1f5f9", mb: 1.2 }} />

                {deviceClaims.length === 0 ? (
                  <Stack alignItems="center" py={2.5} spacing={1}>
                    <HistoryIcon sx={{ fontSize: 26, color: "#e2e8f0" }} />
                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                      No claims yet
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    {deviceClaims.map((c) => (
                      <Card
                        key={c.id}
                        elevation={0}
                        onClick={() => onOpenClaim?.(c)}
                        sx={{
                          borderRadius: 0.2,
                          border: "1px solid #f1f5f9",
                          bgcolor: "#fafafa",
                          cursor: "pointer",
                          transition: "background 0.15s ease, box-shadow 0.15s ease",
                          "&:hover": { bgcolor: "#f1f5f9", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
                          "&:active": { bgcolor: "#e8f0fe" },
                        }}
                      >
                        <CardContent sx={{ p: { xs: "10px !important", sm: "12px !important" } }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>

                            <Stack direction="row" spacing={0.8} alignItems="center" flex={1} minWidth={0}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 0.2,
                                  bgcolor: "#f8fafc",
                                  border: "1px solid #e8f0fe",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <TagIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                              </Box>

                              <Box minWidth={0}>
                                <Typography
                                  sx={{
                                    fontSize: { xs: 11, sm: 12 },
                                    fontWeight: 700,
                                    color: "#111827",
                                    fontFamily: "monospace",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Claim #{c.id}
                                </Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <AccessTimeIcon sx={{ fontSize: 11, color: "#94a3b8" }} />
                                  <Typography sx={{ fontSize: { xs: 9, sm: 10 }, color: "#94a3b8" }}>
                                    {formatDateTime(c.created_at)}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Stack>

                            <Stack direction="row" spacing={0.8} alignItems="center" flexShrink={0}>
                              <ClaimStatusChip status={c.status} />
                              <ChevronRightIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                            </Stack>

                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            {/* ══════════════════
                CTA
            ══════════════════ */}
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => onRaiseClaim?.(device.id)}
              sx={{
                borderRadius: 0.2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: { xs: 13, sm: 14 },
                minHeight: 48,
                background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                boxShadow: "0 4px 14px rgba(22,163,74,0.32)",
                "&:hover": {
                  background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
                  boxShadow: "0 6px 18px rgba(22,163,74,0.40)",
                },
                "&:active": { boxShadow: "0 2px 6px rgba(22,163,74,0.2)" },
                transition: "all 0.18s ease",
              }}
            >
              Raise New Claim
            </Button>

          </Stack>
        </Box>
      </Dialog>
    </>
  );
}